
import { useEffect } from "react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { useSocial } from "@/hooks/useSocial";
import { useAuth } from "@/contexts/AuthContext";
import { cn } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { LikeButton } from "./buttons/LikeButton";
import { FavoriteButton } from "./buttons/FavoriteButton";
import { CommentButton } from "./buttons/CommentButton";
import { getSizeConfig } from "./utils/SocialButtonUtils";

interface SocialActionsProps {
  derivativeId: string;
  size?: "xs" | "sm" | "default" | "lg";
  showComments?: boolean;
  onOpenComments?: () => void;
  className?: string;
  orientation?: "horizontal" | "vertical";
  initialCounts?: {
    likes_count?: number;
    favorites_count?: number;
    comments_count?: number;
  };
  disableCommentsDialog?: boolean;
}

export function SocialActions({ 
  derivativeId, 
  size = "default", 
  showComments = true,
  onOpenComments,
  className,
  orientation = "horizontal",
  initialCounts,
  disableCommentsDialog = false
}: SocialActionsProps) {
  const { isAuthenticated, redirectToAuth } = useAuth();
  const { theme } = useTheme();
  
  const { 
    isLiked, 
    isFavorited, 
    counts, 
    toggleLike, 
    toggleFavorite,
    isLoading,
    isAcademy,
    canComment,
    fetchComments
  } = useSocial(derivativeId, initialCounts);
  
  // Ensure we have valid counts (never negative or undefined)
  const commentCount = Math.max(0, counts.comments_count || 0);
  const likeCount = Math.max(0, counts.likes_count || 0);
  const favoriteCount = Math.max(0, counts.favorites_count || 0);

  const handleLike = async () => {
    const result = await toggleLike();
    if (result?.requireAuth) {
      redirectToAuth("Pour aimer cette création, veuillez vous connecter ou créer un compte", window.location.pathname);
    }
  };

  const handleFavorite = async () => {
    const result = await toggleFavorite();
    if (result?.requireAuth) {
      redirectToAuth("Pour ajouter cette création à vos favoris, veuillez vous connecter ou créer un compte", window.location.pathname);
    }
  };

  const handleComments = () => {
    // Pre-fetch comments when comments button is clicked
    fetchComments();
    
    if (!isAuthenticated && !canComment) {
      redirectToAuth("Pour commenter, veuillez vous connecter ou créer un compte", window.location.pathname);
    } else if (onOpenComments && !disableCommentsDialog) {
      // Only open comments dialog if not disabled
      onOpenComments();
    }
  };

  const { buttonSize, iconSize } = getSizeConfig(size);

  // Show loading state while data is being fetched
  if (isLoading && !initialCounts) {
    return (
      <div className={cn(
        "flex items-center justify-center gap-1.5 md:gap-2",
        orientation === "vertical" ? "flex-col" : "",
        className
      )}>
        {Array(showComments ? 3 : 2).fill(0).map((_, i) => (
          <Button key={i} variant="ghost" size="sm" disabled={true} className={buttonSize}>
            <div className="w-3 h-3 rounded-full bg-muted animate-pulse"></div>
          </Button>
        ))}
      </div>
    );
  }

  return (
    <TooltipProvider delayDuration={300}>
      <div className={cn(
        "flex items-center justify-center gap-1.5 md:gap-2",
        orientation === "vertical" ? "flex-col" : "",
        className
      )}>
        <LikeButton
          isLiked={isLiked}
          count={likeCount}
          onClick={handleLike}
          buttonSize={buttonSize}
          iconSize={iconSize}
          theme={theme}
        />

        <FavoriteButton
          isFavorited={isFavorited}
          count={favoriteCount}
          onClick={handleFavorite}
          buttonSize={buttonSize}
          iconSize={iconSize}
          theme={theme}
        />

        {showComments && (
          <CommentButton
            count={commentCount}
            onClick={handleComments}
            buttonSize={buttonSize}
            iconSize={iconSize}
            theme={theme}
            canComment={canComment}
          />
        )}
      </div>
    </TooltipProvider>
  );
}
